import {
  type FlowValueFormat,
  formatCount,
  formatFlowAmount,
} from './flowFormat'
import { type FlowOfFunds, isOtherBucket, nodesByName } from './flowModel'
import { type KindTotals, kindTotals } from './flowTotals'

function plural(count: number, word: string): string {
  return `${formatCount(count)} ${word}${count === 1 ? '' : 's'}`
}

// "8 payers plus others": the "Other payers" bucket is many parties, not one.
function partiesPhrase(
  flow: FlowOfFunds,
  names: Set<string>,
  word: string,
): string {
  const byName = nodesByName(flow)
  const parties = [...names].flatMap((name) => byName.get(name) ?? [])
  const named = parties.filter((node) => !isOtherBucket(node)).length
  const hasOther = parties.length > named
  if (named === 0) return `smaller ${word}s`
  return hasOther ? `${plural(named, word)} plus others` : plural(named, word)
}

function countSuffix(kind: KindTotals): string {
  return kind.count > 0 ? ` (${plural(kind.count, 'transaction')})` : ''
}

// One short sentence per kind of flow shown, e.g. "$3.07M came in from
// 8 payers plus others (519 transactions)."
export function flowSummarySentences(
  flow: FlowOfFunds,
  valueFormat: FlowValueFormat = 'currency',
): string[] {
  const totals = kindTotals(flow)
  const amount = (kind: KindTotals) =>
    formatFlowAmount(kind.amount, valueFormat)
  const sentences: string[] = []
  if (totals.in.amount > 0) {
    sentences.push(
      `${amount(totals.in)} came in from ${partiesPhrase(flow, totals.in.sources, 'payer')}${countSuffix(totals.in)}.`,
    )
  }
  if (totals.between.amount > 0) {
    const transfers =
      totals.between.count > 0
        ? ` in ${plural(totals.between.count, 'transfer')}`
        : ''
    sentences.push(
      `${amount(totals.between)} moved between own accounts${transfers}.`,
    )
  }
  if (totals.out.amount > 0) {
    sentences.push(
      `${amount(totals.out)} went out to ${partiesPhrase(flow, totals.out.targets, 'payee')}${countSuffix(totals.out)}.`,
    )
  }
  return sentences
}
